package com.wintermar.inspection

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHostState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.wintermar.inspection.core.data.AppSetting
import com.wintermar.inspection.core.ui.component.modal.ConfirmationDialog
import com.wintermar.inspection.core.ui.component.modal.util.ModalAction
import com.wintermar.inspection.core.ui.component.snackbar.SnackBar
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarController
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarState
import com.wintermar.inspection.core.ui.theme.AppTheme
import com.wintermar.inspection.util.middleware.PlatformMiddleware
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.widget.menu.TopBarMenuContent
import com.wintermar.inspection.navigation.NavigationController
import com.wintermar.inspection.navigation.NavigationRoot
import com.wintermar.inspection.navigation.Route
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.cancel
import inspection.composeapp.generated.resources.confirm_logout_message
import inspection.composeapp.generated.resources.confirm_logout_title
import inspection.composeapp.generated.resources.yes
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

@Composable
fun App(
    initialRoutes: Route = Route.Splash
) {
    val snackBarHostState = remember { SnackbarHostState() }
    var snackBarState by remember { mutableStateOf<SnackBarState?>(null) }
    var isTopBarVisible by remember { mutableStateOf(false) }
    var navController by remember { mutableStateOf<NavigationController?>(null) }
    val scope = rememberCoroutineScope()
    var isOpenMenu by remember { mutableStateOf(false) }
    val focusManager = LocalFocusManager.current
    var topMenuHeightLayout by remember { mutableStateOf(0.dp) }

    val appSetting: AppSetting = koinInject()
    var inspectionAccess by remember { mutableStateOf(appSetting.inspectionAccess ?: false) }
    var templateAccess by remember { mutableStateOf(appSetting.templateAccess ?: false) }
    var showConfirmationLogout by remember { mutableStateOf(false) }
    val accessibleRoutes = remember(inspectionAccess, templateAccess) {
        buildList {
            if (templateAccess) add(Route.Template)
            if (inspectionAccess) add(Route.Inspection)
        }
    }

    LaunchedEffect(navController?.currentRoute) {
        inspectionAccess = appSetting.inspectionAccess ?: false
        templateAccess = appSetting.templateAccess ?: false
    }

    LaunchedEffect(Unit) {
        SnackBarController.state.collect { event ->
            snackBarState = event
            snackBarHostState.showSnackbar(event.message)
        }
    }

    AppTheme {
        Scaffold(
            topBar = {
                if (isTopBarVisible) {
                    TopMenu(
                        isOpenMenu = isOpenMenu,
                        currentRoute = navController?.currentRoute,
                        onToggle = {
                            isOpenMenu = !isOpenMenu
                        },
                        onHeightChanged = {
                            topMenuHeightLayout = it
                        }
                    )
                }
            },
            snackbarHost = {
                SnackBar(
                    snackBarHostState = snackBarHostState,
                    state = snackBarState
                )
            }
        ) { padding ->
            Box(
                modifier = Modifier.pointerInput(Unit) {
                    detectTapGestures { focusManager.clearFocus() }
                }
            ) {
                PlatformMiddleware(
                    navigationController = navController,
                    content = {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(LocalAppTheme.current.color.bg.normal),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            NavigationRoot(
                                initialBackstack = initialRoutes,
                                paddingValues = padding,
                                onControllerReady = { navController = it },
                                onVisibilityChanged = { showTopBar ->
                                    isTopBarVisible = showTopBar
                                })
                        }
                    }
                )

                TopBarMenuContent(
                    routeMenus = accessibleRoutes,
                    isMenuOpen = isOpenMenu,
                    paddingTop = topMenuHeightLayout,
                    onClick = { route ->
                        scope.launch {
                            isOpenMenu = false
                            delay(200)
                            if (route == Route.Logout) {
                                showConfirmationLogout = true
                                return@launch
                            }
                            navController?.navigateTo(route, true)
                        }
                    },
                    onOpenMenu = {
                        isOpenMenu = true
                    }
                )

                ConfirmationDialog(
                    show = showConfirmationLogout,
                    textAlign = TextAlign.Left,
                    title = stringResource(Res.string.confirm_logout_title),
                    message = stringResource(Res.string.confirm_logout_message),
                    action = ModalAction.default().copy(
                        showAction = true,
                        isDangerAction = false,
                        withBackgroundHeader = true,
                        label = stringResource(Res.string.yes),
                        secondLabel = stringResource(Res.string.cancel),
                        onClick = {
                            showConfirmationLogout = false
                            appSetting.logout()
                            navController?.navigateTo(Route.Auth, true)
                        },
                        secondOnClick = {
                            showConfirmationLogout = false
                        }),
                    onDismiss = {
                        showConfirmationLogout = false
                    }
                )
            }
        }
    }
}

@Composable
expect fun TopMenu(
    isOpenMenu: Boolean = false,
    currentRoute: Route? = null,
    onToggle: () -> Unit,
    onHeightChanged: (Dp) -> Unit
)
